/**
 * DomainStats — раздел «Статистика» (посещённые сайты) в UUID-карточке.
 *
 * Полная статистика с server-side сортировкой / фильтром / пагинацией.
 * Источник: GET /admin/subscriptions/{sid}/domains (агрегат domain_visits из
 * Hysteria-логов нод). По требованию: ТОЛЬКО домен + время + кол-во заходов,
 * IP-адреса не показываем.
 *
 * Ограничение источника (честно для админа, выводим подсказкой): Hysteria
 * логирует домены в основном на оборвавшихся стримах (под РКН-DPI их много →
 * покрытие широкое, но не гарантированный 100% access-log).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card, Table, Tag, Space, Typography, Input, Select, Row, Col, Statistic,
  Button, Alert, App as AntApp,
} from "antd";
import type { TablePaginationConfig } from "antd";
import type { SorterResult } from "antd/es/table/interface";
import { ReloadOutlined, SearchOutlined, GlobalOutlined } from "@ant-design/icons";
import { API_URL } from "../dataProvider";
import type { DomainVisitItem, DomainStatsResponse } from "../types";
import { fmtDateTime } from "../utils";

type SortField = "domain" | "visits" | "first" | "last";
type SortOrder = "asc" | "desc";

// dataIndex таблицы → значение sort для бэкенда.
const COL_TO_SORT: Record<string, SortField> = {
  domain: "domain",
  visits: "visits",
  first_seen: "first",
  last_seen: "last",
};
// обратное — sort бэкенда → ключ колонки (для подсветки активной сортировки).
const SORT_TO_COL: Record<SortField, string> = {
  domain: "domain",
  visits: "visits",
  first: "first_seen",
  last: "last_seen",
};

const DAYS_OPTIONS = [
  { value: 1, label: "За сутки" },
  { value: 7, label: "7 дней" },
  { value: 30, label: "30 дней" },
  { value: 90, label: "90 дней" },
  { value: 365, label: "Год" },
];

export const DomainStats = ({ subscriptionId }: { subscriptionId: number }) => {
  const { message } = AntApp.useApp();
  const [items, setItems] = useState<DomainVisitItem[]>([]);
  const [summary, setSummary] = useState<DomainStatsResponse["summary"] | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [sort, setSort] = useState<SortField>("visits");
  const [order, setOrder] = useState<SortOrder>("desc");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({
        page: String(page),
        per_page: String(perPage),
        sort,
        order,
        days: String(days),
      });
      if (search) q.set("search", search);
      const r = await fetch(
        `${API_URL}/admin/subscriptions/${subscriptionId}/domains?${q.toString()}`,
        { credentials: "include" }
      );
      if (!r.ok) {
        const t = await r.text().catch(() => r.statusText);
        message.error(`Не удалось загрузить статистику: ${r.status} ${t.slice(0, 150)}`);
        return;
      }
      const totalHeader = Number(r.headers.get("x-total-count") || 0);
      const body: DomainStatsResponse = await r.json();
      setItems(body.items || []);
      setSummary(body.summary || null);
      setTotal(totalHeader || body.summary?.unique_domains || (body.items?.length ?? 0));
    } catch (e) {
      message.error(`Сетевая ошибка: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, [subscriptionId, page, perPage, sort, order, search, days, message]);

  useEffect(() => {
    load();
  }, [load]);

  // Debounce поля поиска (300мс) → search → reset page.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const onTableChange = (
    pag: TablePaginationConfig,
    _filters: unknown,
    sorter: SorterResult<DomainVisitItem> | SorterResult<DomainVisitItem>[]
  ) => {
    const so = Array.isArray(sorter) ? sorter[0] : sorter;
    if (so && so.field && so.order) {
      const f = COL_TO_SORT[String(so.field)];
      if (f) {
        setSort(f);
        setOrder(so.order === "ascend" ? "asc" : "desc");
      }
    }
    if (pag.current && pag.current !== page) setPage(pag.current);
    if (pag.pageSize && pag.pageSize !== perPage) {
      setPerPage(pag.pageSize);
      setPage(1);
    }
  };

  const sortedInfo = useMemo(
    () => ({
      columnKey: SORT_TO_COL[sort],
      order: order === "asc" ? ("ascend" as const) : ("descend" as const),
    }),
    [sort, order]
  );

  const hasData = (summary?.unique_domains ?? 0) > 0 || items.length > 0;

  return (
    <Card
      title={
        <Space>
          <GlobalOutlined />
          <span>Статистика сайтов</span>
        </Space>
      }
      size="small"
      extra={
        <Button
          size="small"
          icon={<ReloadOutlined spin={loading} />}
          onClick={load}
        >
          Обновить
        </Button>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        {/* Сводка */}
        <Row gutter={[12, 12]}>
          <Col xs={12} sm={8}>
            <Statistic
              title={`Уникальных сайтов (${days} дн.)`}
              value={summary?.unique_domains ?? 0}
            />
          </Col>
          <Col xs={12} sm={8}>
            <Statistic
              title="Всего заходов"
              value={summary?.total_visits ?? 0}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Typography.Text type="secondary" style={{ fontSize: 14 }}>
              Период данных
            </Typography.Text>
            <div style={{ fontSize: 13, marginTop: 4 }}>
              {summary?.since ? fmtDateTime(summary.since) : "—"}
              {" — "}
              {summary?.until ? fmtDateTime(summary.until) : "—"}
            </div>
          </Col>
        </Row>

        {/* Фильтры */}
        <Space wrap>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Поиск по домену…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ width: 240 }}
          />
          <Select
            value={days}
            onChange={(v) => {
              setDays(v);
              setPage(1);
            }}
            options={DAYS_OPTIONS}
            style={{ width: 130 }}
          />
        </Space>

        {/* Таблица */}
        <Table<DomainVisitItem>
          rowKey="domain"
          loading={loading}
          dataSource={items}
          size="small"
          onChange={onTableChange}
          pagination={{
            current: page,
            pageSize: perPage,
            total,
            showSizeChanger: true,
            pageSizeOptions: [25, 50, 100, 200],
            showTotal: (t) => `${t} сайтов`,
            hideOnSinglePage: false,
          }}
          locale={{
            emptyText: search
              ? "Ничего не найдено по фильтру"
              : "Данных пока нет — появятся в течение ~5 минут после трафика.",
          }}
          columns={[
            {
              title: "Домен",
              dataIndex: "domain",
              key: "domain",
              sorter: true,
              sortOrder: sortedInfo.columnKey === "domain" ? sortedInfo.order : null,
              render: (v: string) => <Typography.Text>{v}</Typography.Text>,
            },
            {
              title: "Заходов",
              dataIndex: "visits",
              key: "visits",
              align: "right",
              width: 110,
              sorter: true,
              sortOrder: sortedInfo.columnKey === "visits" ? sortedInfo.order : null,
              render: (v: number) => <Tag color="blue">{v}</Tag>,
            },
            {
              title: "Первый заход",
              dataIndex: "first_seen",
              key: "first_seen",
              width: 180,
              sorter: true,
              sortOrder: sortedInfo.columnKey === "first_seen" ? sortedInfo.order : null,
              render: (v: string | null) => (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {fmtDateTime(v)}
                </Typography.Text>
              ),
            },
            {
              title: "Последний заход",
              dataIndex: "last_seen",
              key: "last_seen",
              width: 180,
              sorter: true,
              sortOrder: sortedInfo.columnKey === "last_seen" ? sortedInfo.order : null,
              render: (v: string | null) => (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {fmtDateTime(v)}
                </Typography.Text>
              ),
            },
          ]}
        />

        {!hasData && !loading && (
          <Alert
            type="info"
            showIcon
            message="Статистика появится после трафика через VPN"
            description="Домены собираются из логов Hysteria-нод (в основном с оборвавшихся под DPI стримов) и агрегируются раз в 5 минут. Покрытие широкое, но это не 100% access-log. IP-адреса не сохраняются — только домены, время и количество заходов."
          />
        )}
      </Space>
    </Card>
  );
};
